import React, { useState, useEffect } from 'react';
import { X, Loader2, FileDown, CheckCircle, Plus, Upload, Check, ChevronDown, Settings, Palette, AlertCircle, Zap, Trash2, Wrench, Printer, Cpu } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Switch from '../../../components/ui/Switch';
import Icon from '../../../components/AppIcon';
import { componentService } from '../../../services/componentService';
import { manufacturerService } from '../../../services/manufacturerService';
import { productService } from '../../../services/productService';
import { supplierService } from '../../../services/supplierService';
import { sellerProductService } from '../../../services/sellerProductService';
import { manufacturingService } from '../../../services/manufacturingService';
import { extractComponentNamesFromModel } from '../../../utils/gltfParser';
import { useAuth } from '../../../contexts/AuthContext';
import { imageUploadService } from '../../../utils/imageUploadService';
import { supabase } from '../../../lib/supabase';

export default function ComponentModal({ component, onClose, currentUserId, sellerAccountId, selectedProductId }) {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    manufacturerPartNumber: '',
    manufacturerId: '',
    productId: selectedProductId || '',
    supplierId: '',
    specifications: '',
    stock_quantity: 0,
    returnperiod: '',
    purchaseLimit: '',
    coreCharge: '',
    isActive: true,
    condition: 'New',
    customComponentName: '',
    images: [],
    shippingWeight: '',
    shippingLength: '',
    shippingWidth: '',
    shippingHeight: '',
    is3dManufacturing: false,
    preferred3dMethod: '',
    mfg3dTypes: [],
    mfg3dMaterials: [],
    mfg3dColors: {},
    isCncCapable: false,
    cncMaterial: '',
    cncGrade: '',
    mfgCncTypes: [],
    mfgCncMaterials: [],
    mfgCncColors: {},
    isPainted: false, // NEW: Custom painting state
    paintedColors: [], // NEW: Custom painting colors
    stlFilePath: '',
    gltfFilePath: '', // NEW: Visual 3D model file path
    genuinePart: false,
    staffpick: false,
    isSelfManufacturing: true,
    fulfillmentPartnerId: '',
  });

  const [customPaintedColorsRegistry, setCustomPaintedColorsRegistry] = useState([]); // Custom painted colors list
  const [paintedColorName, setPaintedColorName] = useState('');
  const [paintedColorRgb, setPaintedColorRgb] = useState('');
  const [paintedColorMetallicChecked, setPaintedColorMetallicChecked] = useState(false);
  const [paintedColorMetallicValue, setPaintedColorMetallicValue] = useState('1.0');

  const parseColor = (colorStr) => {
    try {
      if (colorStr && typeof colorStr === 'string' && colorStr.startsWith('{') && colorStr.endsWith('}')) {
        return JSON.parse(colorStr);
      }
    } catch (e) {
      console.error('Failed to parse color JSON:', colorStr);
    }
    return { name: colorStr || 'Unnamed Color', rgb: '', isMetallic: false, isPolished: false };
  };

  const deleteCustomPaintedColor = (color) => {
    setCustomPaintedColorsRegistry(prev => prev.filter(c => c !== color));
    setFormData(prev => ({
      ...prev,
      paintedColors: (prev.paintedColors || []).filter(c => c !== color)
    }));
  };

  const handleAddCustomPaintedColor = () => {
    const nameVal = paintedColorName.trim();
    let rgbVal = paintedColorRgb.trim();
    if (!nameVal) return;

    let finalName = nameVal;
    if (!rgbVal) {
      if (!finalName.toLowerCase().endsWith('(natural)')) {
        finalName = `${finalName} (Natural)`;
      }
    }

    const isMetallic = !!paintedColorMetallicChecked;
    const metallicVal = isMetallic ? (parseFloat(paintedColorMetallicValue) || 1.0) : 0;
    const newColorObj = { name: finalName, rgb: rgbVal, isMetallic: isMetallic, metallic: metallicVal, isPolished: false };
    const colorStr = JSON.stringify(newColorObj);

    setFormData(prev => {
      const currentList = prev.paintedColors || [];
      return {
        ...prev,
        paintedColors: currentList.includes(colorStr) ? currentList : [...currentList, colorStr]
      };
    });

    setCustomPaintedColorsRegistry(prev => {
      return prev.includes(colorStr) ? prev : [...prev, colorStr];
    });

    setPaintedColorName('');
    setPaintedColorRgb('');
    setPaintedColorMetallicChecked(false);
    setPaintedColorMetallicValue('1.0');
  };

  const togglePaintedColor = (color) => {
    setFormData(prev => {
      const currentList = prev.paintedColors || [];
      const updatedList = currentList.includes(color)
        ? currentList.filter(c => c !== color)
        : [...currentList, color];
      return {
        ...prev,
        paintedColors: updatedList
      };
    });
  };

  const [displayImageUrls, setDisplayImageUrls] = useState([]);
  const [products, setProducts] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [modelComponentNames, setModelComponentNames] = useState(null);
  const [loadingComponentNames, setLoadingComponentNames] = useState(false);
  const [imageUploadLoading, setImageUploadLoading] = useState(false);

  const [activeTab, setActiveTab] = useState('general');
  const [mfgAccounts, setMfgAccounts] = useState([]);
  const [sellerTier, setSellerTier] = useState('entry');
  const [allMaterials, setAllMaterials] = useState([]);
  const [globalMfgOpt, setGlobalMfgOpt] = useState(null);
  const [partnerConfig, setPartnerConfig] = useState(null);

  // Load global master manufacturing configurations directly from the components parent table!
  useEffect(() => {
    const loadGlobalOptions = async () => {
      const gComponentId = component?.component_id || component?.componentId || component?.globalComponentId || (component && !component.price ? component.id : null);
      if (gComponentId) {
        // Query components table to get the true master configurations
        const { data } = await supabase
          .from('components')
          .select('*')
          .eq('id', gComponentId)
          .maybeSingle();
          
        if (data) {
          setGlobalMfgOpt({
            offered_methods: data.is_3d_manufacturing && data.is_cnc_capable ? 'both' : data.is_3d_manufacturing ? '3d_only' : data.is_cnc_capable ? 'cnc_only' : 'none',
            mfg_3d_types: data.mfg_3d_types || [],
            mfg_3d_materials: data.mfg_3d_materials || [],
            mfg_3d_colors: data.mfg_3d_colors || {},
            mfg_cnc_types: data.mfg_cnc_types || [],
            mfg_cnc_materials: data.mfg_cnc_materials || [],
            mfg_cnc_colors: data.mfg_cnc_colors || {}
          });
        }
      }
    };
    loadGlobalOptions();
  }, [component]);

  // Load selected partner configurations
  useEffect(() => {
    const loadPartnerConfig = async () => {
      const partnerId = formData.fulfillmentPartnerId || formData.manufacturerId;
      const selectedMfg = mfgAccounts.find(m => m.id === partnerId);
      const partnerAccountId = selectedMfg?.manufacturer_account_id;

      if (partnerAccountId && !formData.isSelfManufacturing) {
        const { data } = await supabase
          .from('manufacturing_supplier_configs')
          .select('*')
          .eq('manufacturer_id', partnerAccountId)
          .maybeSingle();
        setPartnerConfig(data);
      } else {
        setPartnerConfig(null);
      }
    };
    loadPartnerConfig();
  }, [formData.fulfillmentPartnerId, formData.manufacturerId, formData.isSelfManufacturing, mfgAccounts]);

  const allowed3dTypes = React.useMemo(() => {
    const masterTypes = globalMfgOpt?.mfg_3d_types || [];
    if (formData.isSelfManufacturing || !partnerConfig) {
      return masterTypes;
    }
    const partnerTypes = partnerConfig.capabilities?.machines || [];
    if (partnerTypes.length === 0) return masterTypes;
    return masterTypes.filter(type => partnerTypes.includes(type));
  }, [globalMfgOpt, partnerConfig, formData.isSelfManufacturing]);

  const allowed3dMaterials = React.useMemo(() => {
    const masterMaterials = globalMfgOpt?.mfg_3d_materials || [];
    if (formData.isSelfManufacturing || !partnerConfig) {
      return masterMaterials;
    }
    const partnerMaterials = partnerConfig.capabilities?.materials || [];
    if (partnerMaterials.length === 0) return masterMaterials;
    return masterMaterials.filter(mat => {
      const matName = typeof mat === 'string' ? mat : mat.name;
      return partnerMaterials.includes(matName);
    });
  }, [globalMfgOpt, partnerConfig, formData.isSelfManufacturing]);

  const allowed3dColors = React.useMemo(() => {
    const masterColors = globalMfgOpt?.mfg_3d_colors || {};
    if (formData.isSelfManufacturing || !partnerConfig) {
      return masterColors;
    }
    
    const partnerColors = partnerConfig.capabilities?.colors || {};
    const intersection = {};
    const mats = globalMfgOpt?.mfg_3d_materials || [];
    mats.forEach(m => {
      const matName = typeof m === 'string' ? m : m.name;
      const mColors = masterColors[matName] || [];
      const pColors = partnerColors[matName] || [];
      if (pColors.length === 0) {
        intersection[matName] = mColors;
      } else {
        intersection[matName] = mColors.filter(color => pColors.includes(color));
      }
    });
    return intersection;
  }, [globalMfgOpt, partnerConfig, formData.isSelfManufacturing]);

  const allowedCncTypes = React.useMemo(() => {
    const masterTypes = globalMfgOpt?.mfg_cnc_types || [];
    if (formData.isSelfManufacturing || !partnerConfig) {
      return masterTypes;
    }
    const partnerTypes = partnerConfig.capabilities?.machines || [];
    if (partnerTypes.length === 0) return masterTypes;
    return masterTypes.filter(type => partnerTypes.includes(type));
  }, [globalMfgOpt, partnerConfig, formData.isSelfManufacturing]);

  const allowedCncMaterials = React.useMemo(() => {
    const masterMaterials = globalMfgOpt?.mfg_cnc_materials || [];
    if (formData.isSelfManufacturing || !partnerConfig) {
      return masterMaterials;
    }
    const partnerMaterials = partnerConfig.capabilities?.materials || [];
    if (partnerMaterials.length === 0) return masterMaterials;
    return masterMaterials.filter(mat => {
      const matName = typeof mat === 'string' ? mat : mat.name;
      return partnerMaterials.includes(matName);
    });
  }, [globalMfgOpt, partnerConfig, formData.isSelfManufacturing]);

  const allowedCncColors = React.useMemo(() => {
    const masterColors = globalMfgOpt?.mfg_cnc_colors || {};
    if (formData.isSelfManufacturing || !partnerConfig) {
      return masterColors;
    }
    const partnerColors = partnerConfig.capabilities?.colors || {};
    const intersection = {};
    const mats = globalMfgOpt?.mfg_cnc_materials || [];
    mats.forEach(m => {
      const matName = typeof m === 'string' ? m : m.name;
      const mColors = masterColors[matName] || [];
      const pColors = partnerColors[matName] || [];
      if (pColors.length === 0) {
        intersection[matName] = mColors;
      } else {
        intersection[matName] = mColors.filter(color => pColors.includes(color));
      }
    });
    return intersection;
  }, [globalMfgOpt, partnerConfig, formData.isSelfManufacturing]);

  const hasMismatchedSpecs = React.useMemo(() => {
    if (!globalMfgOpt) return false;
    
    const cncTypes = globalMfgOpt.mfg_cnc_types || [];
    const t3dTypes = globalMfgOpt.mfg_3d_types || [];
    const cncMats = globalMfgOpt.mfg_cnc_materials || [];
    const t3dMats = globalMfgOpt.mfg_3d_materials || [];

    if (formData.is3dManufacturing) {
      const invalidTypes = formData.mfg3dTypes?.some(t => !t3dTypes.includes(t));
      const invalidMats = formData.mfg3dMaterials?.some(m => !t3dMats.includes(m));
      if (invalidTypes || invalidMats) return true;
    }
    
    if (formData.isCncCapable) {
      const invalidTypes = formData.mfgCncTypes?.some(t => !cncTypes.includes(t));
      const invalidMats = formData.mfgCncMaterials?.some(m => !cncMats.includes(m));
      if (invalidTypes || invalidMats) return true;
    }

    return false;
  }, [globalMfgOpt, formData]);

  const [isMetric, setIsMetric] = useState(false);
  const [isSelfManufacturer, setIsSelfManufacturer] = useState(false);
  const [isSelfSupplier, setIsSelfSupplier] = useState(true);
  const [activeColorMaterial, setActiveColorMaterial] = useState(null);
  const [uploadingSTL, setUploadingSTL] = useState(false);
  const [stlFile, setStlFile] = useState(null);
  const [uploadingGLB, setUploadingGLB] = useState(false); // NEW
  const [glbFile, setGlbFile] = useState(null); // NEW
  const [substitutionMode, setSubstitutionMode] = useState(component?.gltf_file_path || component?.gltfFilePath ? 'substitute' : 'standard'); // NEW
  const [isAccessory, setIsAccessory] = useState(component?.is_accessory || component?.isAccessory || false); // Symmetrical: Tracks if this is a custom aftermarket accessory!

  useEffect(() => {
    const fetchMaterialsAndAccounts = async () => {
      try {
        const [mats, mfgRes] = await Promise.all([
          manufacturingService.getMaterials(true),
          supabase.from('manufacturers').select('id, name, manufacturer_account_id').not('manufacturer_account_id', 'is', null)
        ]);
        setAllMaterials(mats || []);
        
        // Map to expected keys so that the drop-down and capabilities render perfectly
        const mappedMfg = (mfgRes.data || []).map(m => ({
          id: m.id,
          business_name: m.name,
          manufacturer_account_id: m.manufacturer_account_id
        }));
        setMfgAccounts(mappedMfg);
        
        if (sellerAccountId) {
          const { data: sellerAcc } = await supabase
            .from('seller_accounts')
            .select('tier')
            .eq('id', sellerAccountId)
            .maybeSingle();
          if (sellerAcc) {
            setSellerTier(sellerAcc.tier || 'entry');
          }
        }
      } catch (err) {
        console.error('Failed to load manufacturing parameters:', err);
      }
    };
    fetchMaterialsAndAccounts();
  }, [sellerAccountId]);

  useEffect(() => {
    if (formData.mfg3dMaterials.length > 0 && (!activeColorMaterial || !formData.mfg3dMaterials.includes(activeColorMaterial))) {
        setActiveColorMaterial(formData.mfg3dMaterials[0]);
    } else if (formData.mfg3dMaterials.length === 0 && formData.mfgCncMaterials.length > 0 && (!activeColorMaterial || !formData.mfgCncMaterials.includes(activeColorMaterial))) {
        setActiveColorMaterial(formData.mfgCncMaterials[0]);
    } else if (formData.mfg3dMaterials.length === 0 && formData.mfgCncMaterials.length === 0) {
        setActiveColorMaterial(null);
    }
  }, [formData.mfg3dMaterials, formData.mfgCncMaterials]);

  const toggleArrayField = (fieldName, value) => {
    setFormData(prev => {
      const current = prev[fieldName] || [];
      const updated = current.includes(value)
        ? current.filter(item => item !== value)
        : [...current, value];
      return { ...prev, [fieldName]: updated };
    });
  };

  const toggleColorForMaterial = (fieldName, materialName, color) => {
    setFormData(prev => {
      const currentMap = prev[fieldName] || {};
      const materialColors = currentMap[materialName] || [];
      const updatedColors = materialColors.includes(color)
        ? materialColors.filter(c => c !== color)
        : [...materialColors, color];
        
      return {
        ...prev,
        [fieldName]: {
          ...currentMap,
          [materialName]: updatedColors
        }
      };
    });
  };

  const getSortedMaterialsByColumn = (materials, numCols = 3) => {
    if (!materials || materials.length === 0) return [];
    const sorted = [...materials].sort((a, b) => {
      const nameA = (typeof a === 'string' ? a : a.name).toLowerCase();
      const nameB = (typeof b === 'string' ? b : b.name).toLowerCase();
      return nameA.localeCompare(nameB);
    });
    const numRows = Math.ceil(sorted.length / numCols);
    const grid = [];
    for (let r = 0; r < numRows; r++) {
      for (let c = 0; c < numCols; c++) {
        const index = c * numRows + r;
        if (index < sorted.length) grid.push(sorted[index]);
      }
    }
    return grid;
  };

  const handleSTLChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.stl')) {
        setError('Please upload a valid .stl file');
        return;
      }
      setStlFile(file);
      setError('');
    }
  };

  const handleDeleteSTL = async () => {
    if (component?.id && formData.stlFilePath) {
      if (!window.confirm('Are you sure you want to delete the manufacturing STL file?')) return;
      setUploadingSTL(true);
      try {
        await componentService.deleteSTL(component.id, formData.stlFilePath);
        setFormData(prev => ({ ...prev, stlFilePath: '' }));
      } catch (err) {
        setError('Failed to delete STL file');
      } finally {
        setUploadingSTL(false);
      }
    } else {
      setStlFile(null);
    }
  };

  const handleGLBChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.glb') && !file.name.toLowerCase().endsWith('.gltf')) {
        setError('Please upload a valid .glb or .gltf file');
        return;
      }
      setGlbFile(file);
      setError('');
    }
  };

  const handleDeleteGLB = async () => {
    const compId = component?.componentId || component?.id;
    if (compId && formData.gltfFilePath) {
      if (!window.confirm('Are you sure you want to delete the visual 3D GLB model file?')) return;
      setUploadingGLB(true);
      try {
        await supabase.from('components').update({ gltf_file_path: null }).eq('id', compId);
        setFormData(prev => ({ ...prev, gltfFilePath: '' }));
      } catch (err) {
        setError('Failed to delete GLB file');
      } finally {
        setUploadingGLB(false);
      }
    } else {
      setGlbFile(null);
    }
  };

  useEffect(() => {
    const fetchMaterials = async () => {
      try {
        const mats = await manufacturingService.getMaterials(true);
        setAllMaterials(mats || []);
      } catch (err) {
        console.error('Failed to fetch standardized materials:', err);
      }
    };
    fetchMaterials();
  }, []);

  useEffect(() => {
    if (component) {
      const isSelfMfg = component.isSelfManufacturing || !component.manufacturerId;
      const isSelfSup = !component.supplierId;
      const isAcc = component.isAccessory || component.is_accessory || false;
      
      setIsSelfManufacturer(isSelfMfg);
      setIsSelfSupplier(isSelfSup);
      setIsAccessory(isAcc);

      // Symmetrical Catalog Swap Dropdown Auto-Sync:
      // If the component is saved as an aftermarket accessory or carries an active visual substitute GLB model,
      // we must set substitutionMode to 'substitute' on mount so that the model uploaders/swaps are fully shown!
      if (isAcc || component.gltfFilePath || component.gltf_file_path) {
        setSubstitutionMode('substitute');
      } else {
        setSubstitutionMode('standard');
      }
    } else {
      setIsSelfManufacturer(true);
      setIsSelfSupplier(true);
      setIsAccessory(false);
      setSubstitutionMode('standard');
    }
  }, [component]);

  useEffect(() => {
    loadData();
    if (component) {
      setFormData({
        name: component?.globalName || component?.name || '',
        description: component?.description || '',
        price: component?.price ? Number(component.price).toString() : '',
        manufacturerPartNumber: component?.manufacturerPartNumber || '',
        manufacturerId: component?.manufacturerId || '',
        productId: component?.productId || '',
        supplierId: component?.supplierId || '',
        specifications: JSON.stringify(component?.specifications || {}, null, 2),
        stock_quantity: component?.stockQuantity || component?.stock_quantity || 0,
        returnperiod: component?.returnperiod ?? '',
        purchaseLimit: component?.purchaseLimit || '',
        coreCharge: component?.coreCharge || '',
        is3dManufacturing: false,
        preferred3dMethod: component?.preferred3dMethod || '',
        mfg3dTypes: [],
        mfg3dMaterials: [],
        mfg3dColors: {},
        isCncCapable: false,
        cncMaterial: component?.cncMaterial || '',
        cncGrade: component?.cncGrade || '',
        mfgCncTypes: [],
        mfgCncMaterials: [],
        mfgCncColors: {},
        stlFilePath: component?.stlFilePath || '',
        gltfFilePath: component?.gltfFilePath || component?.gltf_file_path || '', // NEW
        isActive: component?.isActive ?? true, 
        condition: component?.condition || 'New',
        customComponentName: component?.customName || '',
        images: component?.images || [],
        shippingWeight: component?.shippingWeight || '',
        shippingLength: component?.shippingLength || '',
        shippingWidth: component?.shippingWidth || '',
        shippingHeight: component?.shippingHeight || '',
        genuinePart: component?.genuinePart || false,
        staffpick: component?.staffpick || false,
        isSelfManufacturing: true,
        fulfillmentPartnerId: '',
      });

      // Load normalized manufacturing options from the database
      supabase
        .from('manufacturing_options')
        .select('*')
        .eq('seller_component_id', component.id)
        .maybeSingle()
        .then(({ data: mfgOpt }) => {
          if (mfgOpt) {
            setFormData(prev => ({
              ...prev,
              isSelfManufacturing: typeof component.isSelfManufacturing === 'boolean' ? component.isSelfManufacturing : true,
              fulfillmentPartnerId: component.manufacturerId || component.fulfillmentPartnerId || '',
              is3dManufacturing: mfgOpt.offered_methods === '3d_only' || mfgOpt.offered_methods === 'both',
              mfg3dTypes: mfgOpt.mfg_3d_types || [],
              mfg3dMaterials: mfgOpt.mfg_3d_materials || [],
              mfg3dColors: mfgOpt.mfg_3d_colors || {},
              isCncCapable: mfgOpt.offered_methods === 'cnc_only' || mfgOpt.offered_methods === 'both',
              mfgCncTypes: mfgOpt.mfg_cnc_types || [],
              mfgCncMaterials: mfgOpt.mfg_cnc_materials || [],
              mfgCncColors: mfgOpt.mfg_cnc_colors || {},
              isPainted: mfgOpt.is_painted || false,
              paintedColors: Array.isArray(mfgOpt.painted_colors) ? mfgOpt.painted_colors : []
            }));

            // Symmetrically restore customPaintedColorsRegistry on load!
            const m3d = mfgOpt.mfg_3d_colors || {};
            const savedRegistry = m3d['__customPaintedRegistry'] || [];

            const standardColors = ['Black', 'White', 'Gray', 'Red', 'Blue', 'Green', 'Yellow', 'Silver', 'Gold', 'Clear'];
            const savedColors = Array.isArray(mfgOpt.painted_colors) ? mfgOpt.painted_colors : [];
            const customList = savedColors.filter(c => {
              const name = parseColor(c).name;
              return !standardColors.includes(name);
            });
            
            // Merge with saved registry to preserve inactive custom paint colors across sessions!
            setCustomPaintedColorsRegistry([...new Set([...customList, ...savedRegistry])]);
          }
        });
    } else if (selectedProductId) {
      setFormData(prev => ({ ...prev, productId: selectedProductId }));
    }
  }, [component, selectedProductId]);

  const loadData = async () => {
    try {
      const [mfgRes, supRes, sellerProds] = await Promise.all([
        manufacturerService?.getAll().catch(() => []),
        supplierService?.getAll().catch(() => []),
        sellerAccountId ? sellerProductService.getSellerProductsBySellerId(sellerAccountId).catch(() => []) : Promise.resolve([])
      ]);
      const extract = (res) => {
        if (Array.isArray(res)) return res;
        if (res?.data && Array.isArray(res.data)) return res.data;
        return [];
      };
      setManufacturers(extract(mfgRes));
      setSuppliers(extract(supRes));

      // Map and deduplicate the seller's products for the dropdown
      const sps = extract(sellerProds);
      const uniqueProductsMap = new Map();
      sps.forEach(sp => {
        if (sp.productId && !uniqueProductsMap.has(sp.productId)) {
          uniqueProductsMap.set(sp.productId, {
            id: sp.productId,
            name: sp.productName || 'Unknown Product',
            modelUrl: sp.productGltfFilePath ? supabase.storage.from('product-3d-models').getPublicUrl(sp.productGltfFilePath).data.publicUrl : null,
            gltf_file_path: sp.productGltfFilePath // Needed for loadModelComponentNames fallback
          });
        }
      });
      setProducts(Array.from(uniqueProductsMap.values()));
    } catch (err) {
      console.error('Error loading modal data:', err);
    }
  };

  const loadModelComponentNames = async () => {
    if (!formData?.productId || loadingComponentNames) return;
    try {
      setLoadingComponentNames(true);
      const selectedProduct = products?.find(p => p?.id?.toString() === formData?.productId?.toString());
      const modelUrl = selectedProduct?.modelUrl || (selectedProduct?.gltf_file_path ? supabase.storage.from('product-3d-models').getPublicUrl(selectedProduct.gltf_file_path).data.publicUrl : null);
      
      if (!modelUrl) {
        setModelComponentNames([]);
        return;
      }
      const materialNames = await extractComponentNamesFromModel(modelUrl);
      setModelComponentNames(materialNames || []);
    } catch (err) {
      console.error('Failed to extract component names:', err);
      setModelComponentNames([]);
    } finally {
      setLoadingComponentNames(false);
    }
  };

  useEffect(() => {
    if (formData?.productId && modelComponentNames === null && !loadingComponentNames) {
      loadModelComponentNames();
    }
  }, [formData?.productId, modelComponentNames, loadingComponentNames]);

  useEffect(() => {
    const generateDisplayUrls = async () => {
      if (!formData.images || formData.images.length === 0) {
        setDisplayImageUrls([]);
        return;
      }
      const urls = formData.images.map(path => path.startsWith('http') ? path : imageUploadService.getPublicUrl(path));
      setDisplayImageUrls(urls.filter(url => url !== null));
    };
    generateDisplayUrls();
  }, [formData.images]);

  const handleImageFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    if (formData.images.length + files.length > 25) {
      setError(`Max 25 images. You have ${formData.images.length}.`);
      return;
    }
    setImageUploadLoading(true);
    setError('');
    try {
      const tempId = component?.id || `new-${Date.now()}`;
      const folderPath = imageUploadService.getComponentImagePath(tempId);
      const uploadedPaths = await imageUploadService.uploadImages(files, folderPath);
      setFormData(prev => ({ ...prev, images: [...prev.images, ...uploadedPaths] }));
    } catch (err) {
      setError(`Upload failed: ${err.message}`);
    } finally {
      setImageUploadLoading(false);
      e.target.value = null;
    }
  };

  const handleRemoveImage = (imagePathToRemove) => {
    setFormData(prev => ({ ...prev, images: prev.images.filter(path => path !== imagePathToRemove) }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError('');

    try {
      let parsedSpecifications = null;
      try {
        if (formData?.specifications && formData?.specifications?.trim() !== '') {
          parsedSpecifications = JSON.parse(formData?.specifications);
        }
      } catch (jsonError) {
        setError('Invalid JSON format for specifications.');
        setLoading(false);
        return;
      }

      if (formData.is3dManufacturing) {
        if (formData.mfg3dTypes.length === 0) { setError('Select at least one 3D Technology.'); setLoading(false); return; }
        if (formData.mfg3dMaterials.length === 0) { setError('Select at least one Print Material.'); setLoading(false); return; }
        for (const mat of formData.mfg3dMaterials) {
            const libMat = allMaterials.find(m => (typeof m === 'string' ? m : m.name) === mat);
            if (libMat?.colors?.length > 0 && (!formData.mfg3dColors[mat] || formData.mfg3dColors[mat].length === 0)) {
                setError(`Select at least one color for: ${mat}`); setLoading(false); return;
            }
        }
      }

      if (formData.isCncCapable) {
        if (formData.mfgCncTypes.length === 0) { setError('Select at least one CNC Technology.'); setLoading(false); return; }
        if (formData.mfgCncMaterials.length === 0) { setError('Select at least one CNC Material.'); setLoading(false); return; }
        for (const mat of formData.mfgCncMaterials) {
            const libMat = allMaterials.find(m => (typeof m === 'string' ? m : m.name) === mat);
            if (libMat?.colors?.length > 0 && (!formData.mfgCncColors[mat] || formData.mfgCncColors[mat].length === 0)) {
                setError(`Select at least one color for: ${mat}`); setLoading(false); return;
            }
        }
      }

      const submitData = {
        ...formData,
        is_accessory: isAccessory, // Symmetrical: Write custom accessory status flag natively to database!
        manufacturerId: isSelfManufacturer ? null : (formData?.fulfillmentPartnerId || formData?.manufacturerId || null),
        supplierId: isSelfSupplier ? null : (formData?.supplierId || null),
        is_self_manufacturing: isSelfManufacturer,
        productId: formData?.productId || null,
        specifications: parsedSpecifications,
        customName: formData?.customComponentName || null,
        stock_quantity: parseInt(formData?.stock_quantity, 10) || 0,
        returnperiod: formData?.returnperiod === '' ? null : parseInt(formData.returnperiod, 10),
        purchaseLimit: formData?.purchaseLimit === '' ? null : parseInt(formData.purchaseLimit, 10),
        coreCharge: formData?.coreCharge === '' ? 0 : parseFloat(formData.coreCharge),
        shippingWeight: parseFloat(formData?.shippingWeight) || 0,
        shippingLength: parseFloat(formData?.shippingLength) || 0,
        shippingWidth: parseFloat(formData?.shippingWidth) || 0,
        shippingHeight: parseFloat(formData?.shippingHeight) || 0,
        sellerId: sellerAccountId,
        ...((!component && user?.id) && { createdBy: user?.id })
      };

      let savedComponent;
      if (component) {
        savedComponent = await componentService?.update(component?.id, submitData);
      } else {
        savedComponent = await componentService?.create(submitData);
      }

      // Save to normalized manufacturing_options table
      let offeredMethods = 'none';
      if (formData.isSelfManufacturing) {
        if (formData.is3dManufacturing && formData.isCncCapable) {
          offeredMethods = 'both';
        } else if (formData.is3dManufacturing) {
          offeredMethods = '3d_only';
        } else if (formData.isCncCapable) {
          offeredMethods = 'cnc_only';
        }
      }

      const mfg3dColorsPayload = { ...(formData.mfg3dColors || {}) };
      mfg3dColorsPayload['__customPaintedRegistry'] = customPaintedColorsRegistry;

      const mfgOptionsPayload = {
        seller_component_id: component?.id || savedComponent?.id,
        offered_methods: offeredMethods,
        mfg_3d_types: formData.mfg3dTypes || [],
        mfg_3d_materials: formData.mfg3dMaterials || [],
        mfg_3d_colors: mfg3dColorsPayload,
        mfg_cnc_types: formData.mfgCncTypes || [],
        mfg_cnc_materials: formData.mfgCncMaterials || [],
        mfg_cnc_colors: formData.mfgCncColors || {},
        is_painted: !formData.is3dManufacturing && !formData.isCncCapable ? (formData.isPainted || false) : false,
        painted_colors: !formData.is3dManufacturing && !formData.isCncCapable ? (formData.paintedColors || []) : []
      };

      const { error: mfgErr } = await supabase
        .from('manufacturing_options')
        .upsert(mfgOptionsPayload, { onConflict: 'seller_component_id' });

      if (mfgErr) throw mfgErr;

      if (stlFile && (savedComponent?.id || component?.id)) {
        setUploadingSTL(true);
        try {
          const compId = component?.globalComponentId || component?.componentId || savedComponent?.id;
          await componentService.uploadSTL(compId, stlFile);
        } catch (stlErr) {
          console.error('STL upload failed:', stlErr);
        } finally {
          setUploadingSTL(false);
        }
      }

      if (glbFile && (savedComponent?.id || component?.id)) {
        setUploadingGLB(true);
        try {
          const compId = component?.globalComponentId || component?.componentId || savedComponent?.id;
          const filePath = `components/${compId}_${glbFile.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
          
          const { error: uploadErr } = await supabase.storage
            .from('product-3d-models')
            .upload(filePath, glbFile, { upsert: true });
            
          if (uploadErr) throw uploadErr;
          
          const { error: updateErr } = await supabase
            .from('components')
            .update({ stl_file_path: filePath })
            .eq('id', compId);
            
          if (updateErr) throw updateErr;
        } catch (glbErr) {
          console.error('GLB upload failed:', glbErr);
        } finally {
          setUploadingGLB(false);
        }
      }
      onClose(true);
    } catch (err) {
      setError(err?.message || 'Failed to save component');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    let finalValue = type === 'checkbox' ? checked : value;
    
    // Ensure price and stock are treated as raw numbers for editing
    if (name === 'price' || name === 'stock_quantity' || name === 'returnperiod' || name === 'purchaseLimit' || name === 'coreCharge') {
        finalValue = value; // Keep as string for input, but ensure no formatting is applied
    }
    
    setFormData(prev => ({ ...prev, [name]: finalValue }));
  };

  const handleSelectChange = (name) => (newValue) => {
    if (name === 'productId' && newValue !== formData.productId) {
      setModelComponentNames(null);
      setFormData(prev => ({ ...prev, name: '' }));
    }
    // Symmetrical Auto-Toggle: If they select a specific manufacturer from dropdown, uncheck self-manufacturing!
    if (name === 'manufacturerId' && newValue) {
      setIsSelfManufacturer(false);
    }
    // Symmetrical Auto-Toggle: If they select a specific supplier, uncheck self-supplying!
    if (name === 'supplierId' && newValue) {
      setIsSelfSupplier(false);
    }
    setFormData(prev => ({ ...prev, [name]: newValue }));
  };

  const componentNameOptions = (() => {
    const names = modelComponentNames || (formData.name ? [formData.name] : []);
    return names.map(n => ({ value: n, label: n }));
  })();

  const manufacturerOptions = manufacturers?.map(m => ({ value: m?.id?.toString(), label: m?.name })) || [];
  const productOptions = products?.map(p => ({ value: p?.id?.toString(), label: p?.name })) || [];
  const supplierOptions = suppliers?.map(s => ({ value: s?.id?.toString(), label: s?.name })) || [];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[999] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[82vh] flex flex-col relative animate-in fade-in zoom-in-95 duration-200 overflow-hidden">
        
        {loadingComponentNames && (
          <div className="absolute inset-0 bg-white/95 flex flex-col items-center justify-center z-[110] rounded-xl">
            <Loader2 className="w-12 h-12 text-blue-600 animate-spin mb-4" />
            <p className="text-lg font-semibold text-gray-900 mb-2">Extracting Part Data</p>
            <p className="text-sm text-gray-600">Please wait while we parse the model selection...</p>
          </div>
        )}

        <div className="flex items-center justify-between p-6 border-b shrink-0 bg-white">
          <h2 className="text-xl font-semibold text-gray-900">
            {component ? 'Edit Component' : 'Add Component'}
          </h2>
          <button onClick={() => onClose(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
            <X className="w-6 h-6 text-gray-400" />
          </button>
        </div>

        {/* Tab Headers */}
        <div className="flex border-b border-gray-200 px-6 bg-gray-50 shrink-0">
          <button
            type="button"
            onClick={() => setActiveTab('general')}
            className={`py-3 px-4 font-semibold text-sm border-b-2 transition-colors ${
              activeTab === 'general' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            General Info
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('manufacturing')}
            className={`py-3 px-4 font-semibold text-sm border-b-2 transition-colors ${
              activeTab === 'manufacturing' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center gap-2">
                Manufacturing {(formData.is3dManufacturing || formData.isCncCapable) && <div className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />}
            </div>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden bg-white">
          <div className="flex-1 overflow-y-auto p-6 scrollbar-thin">
            <div className={activeTab === 'general' ? 'block space-y-6' : 'hidden'}>
                <div className="flex items-center">
                    <Switch id="isActive" checked={formData?.isActive} onCheckedChange={(checked) => handleChange({ target: { name: 'isActive', type: 'checkbox', checked } })} />
                    <span className="ml-3 text-sm font-medium text-gray-900">Component is Active</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Linked Product</label>
                        <Select name="productId" value={formData?.productId?.toString() || ''} onChange={handleSelectChange('productId')} options={productOptions} placeholder="Select product" disabled={!!component || !!selectedProductId} searchable={true} />
                    </div>

                    {/* Symmetrical Catalog Swap & Substitution Mode Selector - Relocated between Linked Product and Component Name! */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-bold text-gray-700 mb-1">Catalog Swap & Substitution Mode</label>
                      <select
                        name="substitutionMode"
                        value={substitutionMode}
                        onChange={(e) => {
                          setSubstitutionMode(e.target.value);
                          if (e.target.value === 'standard') {
                            handleDeleteGLB();
                          }
                        }}
                        className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-500 outline-none font-medium text-gray-900"
                      >
                        <option value="standard">Original Replacement Part (Seller supplies original design; no 3D visual swap needed)</option>
                        <option value="substitute">Aftermarket Substitute (Seller supplies custom design; visual 3D .glb swap active)</option>
                      </select>
                      <p className="text-[11px] text-gray-500 mt-1 leading-normal">
                        Choose whether this component replaces the base part visually in the 3D model catalog, or is sold as a standard original replacement.
                      </p>
                    </div>

                    {/* Symmetrical Custom Aftermarket Accessory Switch */}
                    <div className="md:col-span-2 flex items-center bg-gray-50 p-3 rounded-lg border border-gray-200">
                      <Switch 
                        id="isAccessory" 
                        checked={isAccessory} 
                        onCheckedChange={(checked) => {
                          setIsAccessory(checked);
                          if (checked) {
                            setSubstitutionMode('substitute'); // Symmetrically force visual 3D swap mode if it's an accessory!
                            // Pre-fill accessory placeholder
                            setFormData(prev => ({ ...prev, name: '' }));
                          } else {
                            setFormData(prev => ({ ...prev, name: '' }));
                          }
                        }} 
                      />
                      <div className="ml-3 text-left">
                        <label htmlFor="isAccessory" className="block text-xs font-bold text-gray-900 uppercase">This is an Aftermarket Accessory</label>
                        <span className="block text-[10px] text-gray-500 leading-normal">If enabled, this item does not replace any base model panel and will be listed natively under "Accessories" in 3D Swap Shop Studio!</span>
                      </div>
                    </div>

                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {isAccessory ? 'Custom Accessory Name *' : 'Component Name *'}
                        </label>
                        {isAccessory ? (
                            <Input type="text" name="name" value={formData?.name} onChange={handleChange} required placeholder="Enter custom accessory name (e.g. Spoiler)" />
                        ) : (
                            modelComponentNames === null || modelComponentNames.length > 0 ? (
                                <Select name="name" value={formData?.name} onChange={handleSelectChange('name')} options={componentNameOptions} placeholder="Select a component" required loading={loadingComponentNames} searchable={true} onOpenChange={(isOpen) => isOpen && modelComponentNames === null && loadModelComponentNames()} />
                            ) : (
                                <Input type="text" name="name" value={formData?.name} onChange={handleChange} required placeholder="Enter custom name" />
                            )
                        )}
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Custom Display Name (Override)</label>
                        <Input type="text" name="customComponentName" value={formData?.customComponentName} onChange={handleChange} placeholder="Optional override name" />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea name="description" value={formData?.description} onChange={handleChange} rows="3" className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Enter part description" />
                    </div>

                    <div className="md:col-span-2">
                    <details className="group bg-gray-50 rounded-lg border border-gray-200">
                        <summary className="flex items-center justify-between p-3 cursor-pointer list-none font-bold text-xs uppercase tracking-widest text-gray-500">
                            <span className="flex items-center gap-2"><Settings className="w-4 h-4" /> Technical Specifications (Optional)</span>
                            <ChevronDown className="w-4 h-4 group-open:rotate-180 transition-transform" />
                        </summary>
                        <div className="p-3 pt-0 border-t border-gray-200 bg-white rounded-b-lg">
                            <textarea name="specifications" value={formData?.specifications} onChange={handleChange} rows="4" className="w-full px-3 py-2 border-0 focus:ring-0 font-mono text-xs" placeholder='{"Weight": "1.2kg"}' />
                            <p className="text-[10px] text-gray-400 mt-1 italic font-bold">Use JSON format: {"{ \"Key\": \"Value\" }"}</p>
                        </div>
                    </details>
                    </div>

                    <div className="relative w-full">
                      <Input label="Price" type="number" name="price" value={formData?.price} onChange={handleChange} required step="0.01" min="0" className="pl-7" />
                      <span className="absolute left-3 top-[38px] text-gray-500 font-medium">$</span>
                    </div>
                    <Input label="Stock Quantity" type="number" name="stock_quantity" value={formData?.stock_quantity} onChange={handleChange} required min="0" />
                    <Select label="Condition" name="condition" value={formData?.condition} onChange={handleSelectChange('condition')} options={[ { value: 'New', label: 'New' }, { value: 'Used', label: 'Used' }, { value: 'Refurbished', label: 'Refurbished' } ]} required />
                    <Input label="Return (Days)" type="number" name="returnperiod" value={formData?.returnperiod} onChange={handleChange} min="0" />

                    <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <label className="block text-sm font-medium text-gray-700">Manufacturer</label>
                                <label className="flex items-center text-xs text-blue-600 cursor-pointer">
                                    <input 
                                      type="checkbox" 
                                      className="mr-1 h-3 w-3 rounded border-gray-300" 
                                      checked={isSelfManufacturer} 
                                      onChange={(e) => {
                                        setIsSelfManufacturer(e.target.checked);
                                        if (e.target.checked) {
                                          setFormData(prev => ({ ...prev, manufacturerId: '' }));
                                        }
                                      }} 
                                    /> I manufacture this
                                </label>
                            </div>
                            <Select name="manufacturerId" value={formData?.manufacturerId?.toString() || ''} onChange={handleSelectChange('manufacturerId')} options={manufacturerOptions} placeholder="Select manufacturer" disabled={isSelfManufacturer} searchable={true} />
                        </div>
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <label className="block text-sm font-medium text-gray-700">Supplier</label>
                                <label className="flex items-center text-xs text-blue-600 cursor-pointer">
                                    <input 
                                      type="checkbox" 
                                      className="mr-1 h-3 w-3 rounded border-gray-300" 
                                      checked={isSelfSupplier} 
                                      onChange={(e) => {
                                        setIsSelfSupplier(e.target.checked);
                                        if (e.target.checked) {
                                          setFormData(prev => ({ ...prev, supplierId: '' }));
                                        }
                                      }} 
                                    /> I supply this
                                </label>
                            </div>
                            <Select name="supplierId" value={formData?.supplierId?.toString() || ''} onChange={handleSelectChange('supplierId')} options={supplierOptions} placeholder="Select supplier" disabled={isSelfSupplier} searchable={true} />
                        </div>
                    </div>

                    <div className="md:col-span-2 border-t pt-6 mt-2">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">Shipping Dimensions</h3>
                            <div className="flex items-center space-x-2 bg-gray-50 p-1 rounded-md border">
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${!isMetric ? 'bg-white shadow-sm text-blue-600' : 'text-gray-400'}`}>Imperial</span>
                                <Switch checked={isMetric} onCheckedChange={setIsMetric} />
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${isMetric ? 'bg-white shadow-sm text-blue-600' : 'text-gray-400'}`}>Metric</span>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <Input label={`Weight (${isMetric ? 'kg' : 'lbs'})`} type="number" name="shippingWeight" value={formData?.shippingWeight || ''} onChange={handleChange} step="0.1" />
                            <Input label={`Length (${isMetric ? 'cm' : 'in'})`} type="number" name="shippingLength" value={formData?.shippingLength || ''} onChange={handleChange} step="0.1" />
                            <Input label={`Width (${isMetric ? 'cm' : 'in'})`} type="number" name="shippingWidth" value={formData?.shippingWidth || ''} onChange={handleChange} step="0.1" />
                            <Input label={`Height (${isMetric ? 'cm' : 'in'})`} type="number" name="shippingHeight" value={formData?.shippingHeight || ''} onChange={handleChange} step="0.1" />
                        </div>
                    </div>

                    <div className="md:col-span-2 border-t pt-6 mt-2">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Gallery Images (Max 25)</h3>
                        <div className="bg-gray-50 p-4 rounded-lg border-2 border-dashed border-gray-200">
                            <input type="file" multiple accept="image/*" onChange={handleImageFileChange} className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-600 hover:file:bg-blue-100 transition-all" disabled={imageUploadLoading || formData.images.length >= 25} />
                            {imageUploadLoading && <p className="flex items-center text-xs text-blue-600 mt-2 font-bold animate-pulse"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Uploading...</p>}
                            <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4">
                                {displayImageUrls.map((imageUrl, index) => (
                                    <div key={index} className="relative aspect-square rounded-lg overflow-hidden group shadow-sm border">
                                        <img src={imageUrl} alt="Gallery" className="w-full h-full object-cover" />
                                        <button type="button" onClick={() => handleRemoveImage(formData.images[index])} className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Symmetrical Visual 3D Component Model (GLB) Uploader */}
                    {substitutionMode === 'substitute' && (
                      <div className="md:col-span-2 border-t pt-6 mt-2 text-left animate-in slide-in-from-top-2 duration-200">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <Printer className="w-5 h-5 text-blue-600" /> 
                          Visual 3D Component Model (GLB)
                        </h3>
                        {formData.gltfFilePath ? (
                          <div className="flex items-center justify-between bg-white p-3 border rounded-lg shadow-sm">
                            <div className="flex items-center gap-3">
                              <CheckCircle className="w-5 h-5 text-green-500" />
                              <span className="text-xs font-bold text-gray-700 truncate max-w-[200px]">{formData.gltfFilePath.split('/').pop()}</span>
                            </div>
                            <button 
                              type="button" 
                              onClick={handleDeleteGLB} 
                              className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-full"
                              title="Delete GLB File"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        ) : (
                          <label className="cursor-pointer flex flex-col items-center justify-center gap-2 px-6 py-8 bg-white border-2 border-dashed border-blue-200 rounded-xl hover:bg-blue-50 transition-all group">
                            <Upload className="w-8 h-8 text-blue-600 group-hover:scale-110 transition-transform" />
                            <span className="text-sm font-bold text-gray-700">{glbFile ? glbFile.name : 'Upload visual GLB File'}</span>
                            <input type="file" accept=".glb,.gltf" onChange={handleGLBChange} className="hidden" />
                          </label>
                        )}
                        {uploadingGLB && <p className="text-xs text-blue-600 mt-2 font-bold animate-pulse">Processing 3D model...</p>}
                        <p className="text-[10px] text-gray-500 mt-1.5 leading-normal">
                          Upload your custom visual .glb or .gltf file to allow shoppers to interact and swap this aftermarket part onto their vehicles!
                        </p>
                      </div>
                    )}
                </div>
            </div>

            <div className={activeTab === 'manufacturing' ? 'block space-y-8 animate-in fade-in' : 'hidden'}>
                {hasMismatchedSpecs && (
                  <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-xl text-xs font-semibold leading-relaxed mb-6">
                    ⚠️ Attention: The global catalog configurations for this component have changed. Some of your previously configured options are no longer authorized globally. Please review your configurations (greyed-out items indicate disabled options) and update/save your changes to align them.
                  </div>
                )}
                {/* Manufacturer Routing Control */}
                <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm space-y-4">
                  <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <Wrench className="w-5 h-5 text-blue-600" />
                    Manufacturing & Order Routing
                  </h3>
                  <p className="text-xs text-gray-500">
                    Configure whether you self-manufacture (3D Print / CNC Machining) this item locally in your shop, or if orders should be automatically routed to an authorized digital manufacturing partner.
                  </p>

                  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-md bg-blue-50 mt-4">
                    <div>
                      <h4 className="font-semibold text-gray-900 text-sm">Self Manufacturing/Fulfillment</h4>
                      <p className="text-xs text-gray-600">Enable this if you have the local equipment and material inventory to produce this part on-demand.</p>
                    </div>
                    <Switch 
                      checked={formData.isSelfManufacturing} 
                      onCheckedChange={(checked) => setFormData({...formData, isSelfManufacturing: checked})} 
                    />
                  </div>

                  {!formData.isSelfManufacturing && (
                    <div className="mt-4 p-4 border border-gray-200 rounded-lg bg-gray-50 animate-in slide-in-from-top-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Select Fulfillment Partner (3D Print / CNC Account Holder)</label>
                      <select
                        name="fulfillmentPartnerId"
                        value={formData.fulfillmentPartnerId}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white text-gray-900 font-medium"
                      >
                        <option value="">-- Choose An Authorized Manufacturer Account --</option>
                        {mfgAccounts.map(acc => (
                          <option key={acc.id} value={acc.id}>{acc.business_name}</option>
                        ))}
                      </select>
                      {mfgAccounts.length === 0 && (
                        <p className="text-xs text-amber-600 mt-2">No active manufacturing service accounts registered in the database yet.</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Gated Capabilities Config Section */}
                {formData.isSelfManufacturing && (
                  <div className="space-y-6">
                    {sellerTier === 'entry' ? (
                      <div className="bg-amber-50 border border-amber-200 text-amber-800 p-6 rounded-xl text-sm text-center font-semibold">
                        ⚠️ Custom 3D printing & CNC Machining configurations require a Mid-Tier or Platinum subscription plan. Please upgrade your subscription plan in your Account Settings to unlock custom manufacturing!
                      </div>
                    ) : (
                      <>
                        {globalMfgOpt && globalMfgOpt.offered_methods === 'none' && (
                          <div className="bg-amber-50 border border-amber-200 text-amber-800 p-4 rounded-xl text-xs text-center font-medium">
                            ⚠️ This global component is not configured for 3D Printing or CNC machining globally. No manufacturing configurations are available for offering.
                          </div>
                        )}

                        {globalMfgOpt && globalMfgOpt.offered_methods !== 'none' && (
                          <div className="bg-gray-50 p-4 rounded-xl border border-blue-100 shadow-sm mb-6">
                            <label className="block text-sm font-bold text-blue-900 mb-3 flex items-center gap-2"><FileDown className="w-5 h-5 text-blue-600" /> Production Model (STL)</label>
                            {formData.stlFilePath ? (
                                <div className="flex items-center justify-between bg-white p-3 border rounded-lg shadow-sm">
                                    <div className="flex items-center gap-3">
                                        <CheckCircle className="w-5 h-5 text-green-500" />
                                        <span className="text-xs font-bold text-gray-700 truncate max-w-[200px]">{formData.stlFilePath.split('/').pop()}</span>
                                    </div>
                                    <button type="button" onClick={handleDeleteSTL} className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-full"><Trash2 className="w-5 h-5" /></button>
                                </div>
                            ) : (
                                <label className="cursor-pointer flex flex-col items-center justify-center gap-2 px-6 py-8 bg-white border-2 border-dashed border-blue-200 rounded-xl hover:bg-blue-50 transition-all group">
                                    <Upload className="w-8 h-8 text-blue-600 group-hover:scale-110 transition-transform" />
                                    <span className="text-sm font-bold text-gray-700">{stlFile ? stlFile.name : 'Upload STL File'}</span>
                                    <input type="file" accept=".stl" onChange={handleSTLChange} className="hidden" />
                                </label>
                            )}
                            {uploadingSTL && <p className="text-xs text-blue-600 mt-2 font-bold animate-pulse">Processing model...</p>}
                          </div>
                        )}

                        {/* 3D Printing Options */}
                        {true && (
                          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm space-y-4 text-left">
                            {globalMfgOpt && globalMfgOpt.offered_methods !== '3d_only' && globalMfgOpt.offered_methods !== 'both' && (
                              <div className="bg-amber-50 border border-amber-200 text-amber-800 p-4 rounded-xl text-xs font-semibold">
                                ⚠️ 3D Printing has not been enabled globally for this component. Standard sellers cannot configure this offering.
                              </div>
                            )}
                            <div className="flex items-center justify-between border-b pb-3">
                              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                <Printer className="w-5 h-5 text-indigo-600" />
                                3D Print Configurations
                              </h3>
                              <Switch 
                                disabled={globalMfgOpt && globalMfgOpt.offered_methods !== '3d_only' && globalMfgOpt.offered_methods !== 'both'}
                                checked={formData.is3dManufacturing}
                                onCheckedChange={(val) => setFormData({ ...formData, is3dManufacturing: val })}
                              />
                            </div>

                            {formData.is3dManufacturing && (
                              <div className="space-y-4 pt-2">
                                {/* Print types */}
                                <div>
                                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">3D Printer Technology Offered</label>
                                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                    {allowed3dTypes.map(type => {
                                      const isAuthorized = !globalMfgOpt || globalMfgOpt.mfg_3d_types?.includes(type);
                                      return (
                                        <label key={type} className={`flex items-center gap-2 p-2 border rounded text-sm transition-all ${isAuthorized ? 'hover:bg-blue-50 cursor-pointer border-gray-200 bg-white' : 'opacity-40 cursor-not-allowed bg-gray-50 border-gray-200 text-gray-400'}`}>
                                          <input
                                            type="checkbox"
                                            checked={formData.mfg3dTypes?.includes(type)}
                                            onChange={() => isAuthorized && toggleArrayField('mfg3dTypes', type)}
                                            disabled={!isAuthorized}
                                            className="rounded text-blue-600 mr-2 disabled:opacity-50"
                                          />
                                          <span>{type}</span>
                                        </label>
                                      );
                                    })}
                                    {allowed3dTypes.length === 0 && (
                                      <p className="text-xs text-amber-600 col-span-full">No active 3D printing capabilities match the master component configurations.</p>
                                    )}
                                  </div>
                                </div>

                                {/* Standard Materials */}
                                <div>
                                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Standardized Materials</label>
                                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
                                    {allowed3dMaterials.map(mat => {
                                      const matName = typeof mat === 'string' ? mat : mat.name;
                                      const isSelected = formData.mfg3dMaterials?.includes(matName);
                                      const isAuthorized = !globalMfgOpt || globalMfgOpt.mfg_3d_materials?.includes(matName);
                                      return (
                                        <label key={matName} className={`flex items-center gap-2 p-2 border rounded transition-all text-sm ${!isAuthorized ? 'opacity-40 cursor-not-allowed bg-gray-50 border-gray-200 text-gray-400' : isSelected ? 'bg-blue-50 border-blue-300 cursor-pointer' : 'bg-white border-gray-200 hover:border-blue-200 cursor-pointer'}`}>
                                          <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => isAuthorized && toggleArrayField('mfg3dMaterials', matName)}
                                            disabled={!isAuthorized}
                                            className="rounded text-blue-600 mr-2 disabled:opacity-50"
                                          />
                                          <span>{matName}</span>
                                        </label>
                                      );
                                    })}
                                    {allowed3dMaterials.length === 0 && (
                                      <p className="text-xs text-amber-600 col-span-full">No active 3D materials match the master component configurations.</p>
                                    )}
                                  </div>

                                  {/* Color select per selected material */}
                                  {formData.mfg3dMaterials?.length > 0 && (
                                    <div className="mt-4 p-4 bg-gray-50 border rounded-lg space-y-4">
                                      <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider">Configure Offered Colors per Material</label>
                                      {formData.mfg3dMaterials.map(matName => {
                                        const availableColors = allowed3dColors[matName] || [];
                                        return (
                                          <div key={matName} className="space-y-1.5 p-3 bg-white border rounded shadow-sm">
                                            <span className="text-xs font-bold text-gray-800">{matName} Colors</span>
                                            <div className="flex flex-wrap gap-1.5">
                                              {availableColors.map(color => {
                                                const isChecked = formData.mfg3dColors?.[matName]?.includes(color);
                                                return (
                                                  <label key={`${matName}-${color}`} className={`flex items-center gap-1 px-2.5 py-1 border rounded-md cursor-pointer transition-all text-xs ${isChecked ? 'bg-indigo-600 border-indigo-700 text-white' : 'bg-gray-50 border-gray-200 hover:bg-indigo-50'}`}>
                                                    <input
                                                      type="checkbox"
                                                      checked={isChecked || false}
                                                      onChange={() => toggleColorForMaterial('mfg3dColors', matName, color)}
                                                      className="hidden"
                                                    />
                                                    <span>{color}</span>
                                                  </label>
                                                );
                                              })}
                                              {availableColors.length === 0 && (
                                                <p className="text-[10px] text-amber-600 italic">No matching colors available for this material.</p>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* CNC Machining Options */}
                        {true && (
                          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm space-y-4 text-left">
                            {globalMfgOpt && globalMfgOpt.offered_methods !== 'cnc_only' && globalMfgOpt.offered_methods !== 'both' && (
                              <div className="bg-amber-50 border border-amber-200 text-amber-800 p-4 rounded-xl text-xs font-semibold">
                                ⚠️ CNC Machining has not been enabled globally for this component. CNC Machining is not available for offering.
                              </div>
                            )}
                            <div className="flex items-center justify-between border-b pb-3">
                              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                <Cpu className="w-5 h-5 text-orange-600" />
                                CNC Machining Configurations
                              </h3>
                              <Switch 
                                disabled={globalMfgOpt && globalMfgOpt.offered_methods !== 'cnc_only' && globalMfgOpt.offered_methods !== 'both'}
                                checked={formData.isCncCapable}
                                onCheckedChange={(val) => setFormData({ ...formData, isCncCapable: val })}
                              />
                            </div>

                            {formData.isCncCapable && (
                              <div className="space-y-4 pt-2">
                                {/* CNC Types */}
                                <div>
                                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">CNC Process Offered</label>
                                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                    {allowedCncTypes.map(type => {
                                      const isAuthorized = !globalMfgOpt || globalMfgOpt.mfg_cnc_types?.includes(type);
                                      return (
                                        <label key={type} className={`flex items-center gap-2 p-2 border rounded text-sm transition-all ${isAuthorized ? 'hover:bg-blue-50 cursor-pointer border-gray-200 bg-white' : 'opacity-40 cursor-not-allowed bg-gray-50 border-gray-200 text-gray-400'}`}>
                                          <input
                                            type="checkbox"
                                            checked={formData.mfgCncTypes?.includes(type)}
                                            onChange={() => isAuthorized && toggleArrayField('mfgCncTypes', type)}
                                            disabled={!isAuthorized}
                                            className="rounded text-blue-600 mr-2 disabled:opacity-50"
                                          />
                                          <span>{type}</span>
                                        </label>
                                      );
                                    })}
                                    {allowedCncTypes.length === 0 && (
                                      <p className="text-xs text-amber-600 col-span-full">No active CNC machining capabilities match the master component configurations.</p>
                                    )}
                                  </div>
                                </div>

                                {/* CNC Materials & Colors */}
                                <div>
                                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">CNC Machining Materials</label>
                                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
                                    {allowedCncMaterials.map(mat => {
                                      const matName = typeof mat === 'string' ? mat : mat.name;
                                      const isSelected = formData.mfgCncMaterials?.includes(matName);
                                      const isAuthorized = !globalMfgOpt || globalMfgOpt.mfg_cnc_materials?.includes(matName);
                                      return (
                                        <label key={matName} className={`flex items-center gap-2 p-2 border rounded transition-all text-sm ${!isAuthorized ? 'opacity-40 cursor-not-allowed bg-gray-50 border-gray-200 text-gray-400' : isSelected ? 'bg-orange-50 border-orange-300 cursor-pointer' : 'bg-white border-gray-200 hover:border-orange-200 cursor-pointer'}`}>
                                          <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => isAuthorized && toggleArrayField('mfgCncMaterials', matName)}
                                            disabled={!isAuthorized}
                                            className="rounded text-blue-600 mr-2 disabled:opacity-50"
                                          />
                                          <span>{matName}</span>
                                        </label>
                                      );
                                    })}
                                    {allowedCncMaterials.length === 0 && (
                                      <p className="text-xs text-amber-600 col-span-full">No active CNC materials match the master component configurations.</p>
                                    )}
                                  </div>

                                  {/* CNC Grade select per selected material */}
                                  {formData.mfgCncMaterials?.length > 0 && (
                                    <div className="mt-4 p-4 bg-gray-50 border rounded-lg space-y-4">
                                      <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider">Configure Offered Grades/Finishes per Material</label>
                                      {formData.mfgCncMaterials.map(matName => {
                                        const availableColors = allowedCncColors[matName] || [];
                                        return (
                                          <div key={matName} className="space-y-1.5 p-3 bg-white border rounded shadow-sm">
                                            <span className="text-xs font-bold text-gray-800">{matName} Finishes</span>
                                            <div className="flex flex-wrap gap-1.5">
                                              {availableColors.map(color => {
                                                const isChecked = formData.mfgCncColors?.[matName]?.includes(color);
                                                return (
                                                  <label key={`${matName}-${color}`} className={`flex items-center gap-1 px-2.5 py-1 border rounded-md cursor-pointer transition-all ${formData.mfgCncColors?.[matName]?.includes(color) ? 'bg-orange-600 border-orange-700 text-white' : 'bg-gray-50 border-gray-200 hover:bg-orange-50'}`}>
                                                    <input
                                                      type="checkbox"
                                                      checked={isChecked || false}
                                                      onChange={() => toggleColorForMaterial('mfgCncColors', matName, color)}
                                                      className="hidden"
                                                    />
                                                    <span>{color}</span>
                                                  </label>
                                                );
                                              })}
                                              {availableColors.length === 0 && (
                                                <p className="text-[10px] text-amber-600 italic">No matching finishes available for this material.</p>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Custom Painted Options */}
                        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm space-y-4 text-left">
                          {(formData.is3dManufacturing || formData.isCncCapable) && (
                            <div className="bg-amber-50 border border-amber-200 text-amber-800 p-4 rounded-xl text-xs font-semibold">
                              ⚠️ Custom Painted Options cannot be configured when 3D Printing or CNC Machining is active.
                            </div>
                          )}
                          <div className="flex items-center justify-between border-b pb-3">
                            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                              <Palette className="w-5 h-5 text-indigo-600" />
                              Custom Painted Configurations
                            </h3>
                            <Switch 
                              disabled={formData.is3dManufacturing || formData.isCncCapable}
                              checked={formData.isPainted && !formData.is3dManufacturing && !formData.isCncCapable}
                              onCheckedChange={(val) => setFormData({ ...formData, isPainted: val })}
                            />
                          </div>

                          {formData.isPainted && !formData.is3dManufacturing && !formData.isCncCapable && (
                            <div className="space-y-4 pt-2">
                              <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Offered Paint Colors / Finishes</label>
                                <div className="flex flex-wrap gap-1.5">
                                  {(() => {
                                    const standardColors = ['Black', 'White', 'Gray', 'Red', 'Blue', 'Green', 'Yellow', 'Silver', 'Gold', 'Clear'];
                                    const savedColors = Array.isArray(formData.paintedColors) ? formData.paintedColors : [];
                                    const availableColors = [...new Set([
                                      ...standardColors,
                                      ...savedColors,
                                      ...customPaintedColorsRegistry
                                    ])];
                                    
                                    return availableColors.map(color => {
                                      const colorObj = parseColor(color);
                                      const isChecked = formData.paintedColors?.includes(color);
                                      const isCustom = customPaintedColorsRegistry.includes(color) || !standardColors.includes(color);
                                      let bgColor = colorObj.rgb;
                                      if (bgColor && !bgColor.startsWith('#') && bgColor.includes(',')) {
                                        bgColor = `rgb(${bgColor})`;
                                      }
                                      
                                      return (
                                        <div 
                                          key={`paint-${color}`} 
                                          onClick={() => togglePaintedColor(color)}
                                          className={`flex items-center gap-1.5 px-2.5 py-1.5 border rounded-md cursor-pointer transition-all text-xs font-medium ${isChecked ? 'bg-indigo-600 border-indigo-700 text-white' : 'bg-gray-50 border-gray-200 hover:bg-indigo-50'}`}
                                        >
                                          {colorObj.rgb && (
                                            <span 
                                              className="w-3.5 h-3.5 rounded-full border border-white/20 inline-block shadow-inner shrink-0" 
                                              style={{ backgroundColor: bgColor }}
                                            />
                                          )}
                                          <span>{colorObj.name}</span>
                                          {colorObj.metallic !== undefined && parseFloat(colorObj.metallic) > 0 ? (
                                            <span className="text-[9px] opacity-80 uppercase font-bold px-1 bg-black/20 rounded">Metal {colorObj.metallic}</span>
                                          ) : (
                                            colorObj.isMetallic && <span className="text-[9px] opacity-80 uppercase font-bold px-1 bg-black/20 rounded">Metal</span>
                                          )}
                                          {isCustom && (
                                            <button
                                              type="button"
                                              onClick={(e) => {
                                                e.stopPropagation(); // Stop click from propagating to parent div click handler!
                                                deleteCustomPaintedColor(color);
                                              }}
                                              className="ml-1.5 p-0.5 rounded-full hover:bg-black/20 hover:text-white transition-colors flex items-center justify-center text-[10px]"
                                              title="Delete Custom Paint"
                                            >
                                              <X className="w-3 h-3" />
                                            </button>
                                          )}
                                        </div>
                                      );
                                    });
                                  })()}
                                </div>
                              </div>

                              {/* Symmetrical Add Custom Color section */}
                              <div className="border-t pt-2.5 mt-2 flex flex-col sm:flex-row gap-2 items-center">
                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-wider whitespace-nowrap">Add Custom:</span>
                                <input
                                  type="text"
                                  placeholder="Paint Name (e.g. Candy Apple Red)"
                                  value={paintedColorName}
                                  onChange={(e) => setPaintedColorName(e.target.value)}
                                  className="text-[10px] px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-auto flex-1 bg-white text-gray-900"
                                />
                                <input
                                  type="text"
                                  placeholder="RGB / Hex (Optional)"
                                  value={paintedColorRgb}
                                  onChange={(e) => setPaintedColorRgb(e.target.value)}
                                  className="text-[10px] px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-28 bg-white text-gray-900"
                                />
                                {(() => {
                                  let rgbInput = paintedColorRgb.trim();
                                  if (rgbInput) {
                                    if (!rgbInput.startsWith('#') && rgbInput.includes(',')) {
                                      rgbInput = `rgb(${rgbInput})`;
                                    }
                                    return (
                                      <span 
                                        className="w-5 h-5 rounded-full border border-gray-300 shadow-inner shrink-0 animate-in fade-in" 
                                        style={{ backgroundColor: rgbInput }}
                                        title="Live Swatch Preview"
                                      />
                                    );
                                  }
                                  return null;
                                })()}
                                <label className="flex items-center gap-1 text-[10px] font-black text-gray-500 cursor-pointer select-none">
                                  <input
                                    type="checkbox"
                                    checked={paintedColorMetallicChecked}
                                    onChange={(e) => setPaintedColorMetallicChecked(e.target.checked)}
                                    className="rounded text-indigo-600 w-3 h-3 border-gray-300"
                                  />
                                  <span>Metallic</span>
                                </label>
                                {paintedColorMetallicChecked && (
                                  <input
                                    type="number"
                                    min="0"
                                    max="1"
                                    step="0.1"
                                    placeholder="Value (0-1)"
                                    value={paintedColorMetallicValue}
                                    onChange={(e) => setPaintedColorMetallicValue(e.target.value)}
                                    className="text-[10px] px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 w-16 bg-white text-gray-900"
                                  />
                                )}
                                <button
                                  type="button"
                                  onClick={handleAddCustomPaintedColor}
                                  disabled={!paintedColorName.trim()}
                                  className="px-3 py-1.5 bg-slate-800 hover:bg-slate-900 text-white rounded text-[10px] font-bold disabled:opacity-50 h-[28px] flex items-center justify-center shrink-0"
                                >
                                  <Plus className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
            </div>
          </div>

          <div className="p-6 border-t bg-gray-50 flex flex-col gap-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-500 text-red-700 text-sm font-bold flex items-center gap-3 rounded animate-in slide-in-from-top-2">
                <AlertCircle className="w-5 h-5 shrink-0" />
                {error}
              </div>
            )}
            <div className="flex justify-end space-x-3">
              <Button type="button" variant="secondary" onClick={() => onClose(false)} disabled={loading}>Cancel</Button>
              <Button type="submit" disabled={loading}>{loading ? 'Saving...' : component ? 'Update' : 'Create'}</Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
